import React, {useEffect, useCallback} from "react"
import RecaptchaV3ContextProvider from "./RecaptchaV3Context"

/**
 * @typedef {Object} GoogleReCaptchaEnterprise
 * @property {function(string, {action: string}): Promise<string>} execute
 */

/**
 * @typedef {Object} GoogleReCaptcha
 * @property {GoogleReCaptchaEnterprise} enterprise
 */

/**
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.siteKey
 */
const RecaptchaV3Provider = ({children, siteKey}) => {
  useEffect(() => {
    const scriptId = "grecaptcha-v3-script"

    if (document.getElementById(scriptId)) {
      return
    }

    const baseUrl = "https://www.google.com/recaptcha/enterprise.js"

    const script = document.createElement("script")
    script.id = scriptId
    script.src = `${baseUrl}?render=${siteKey}`
    script.async = true
    script.defer = true

    script.onerror = () => {
      console.error("Failed to load reCAPTCHA v3")
    }

    document.head.appendChild(script)
  }, [siteKey])

  /**
   * Execute reCAPTCHA v3 verification
   * @param {string} action - The action name for this verification
   * @returns {Promise<string>} The reCAPTCHA token
   */
  const execute = useCallback(
    /** @param {string} action */
    action => {
      return new Promise((resolve, reject) => {
        if (
          typeof window === "undefined" ||
          !Object.hasOwn(window, "grecaptcha")
        ) {
          reject(new Error("reCAPTCHA has not been loaded yet"))
          return
        }

        /** @type {GoogleReCaptcha} */
        const grecaptcha = /** @type {any} */ (window).grecaptcha

        grecaptcha.enterprise
          .execute(siteKey, {action})
          .then(token => {
            resolve(token)
          })
          .catch(
            /** @param {Error} error */
            error => {
              console.error("reCAPTCHA execution failed:", error)
              reject(error)
            },
          )
      })
    },
    [siteKey],
  )

  return (
    <RecaptchaV3ContextProvider execute={execute}>
      {children}
    </RecaptchaV3ContextProvider>
  )
}

export default RecaptchaV3Provider
